import { useQuery } from '@tanstack/react-query';
import type { AdmissionCareTeamDto, AdmissionPreAuthStatusDto, CareTeamCandidateDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { useRealtimeInvalidate } from '@/lib/realtime';
import type { Admission } from './types';

export const admissionKey = (admissionId: string) => ['admission', admissionId] as const;
export const careTeamKey = (admissionId: string) => ['admission-care-team', admissionId] as const;
export const preAuthKey = (admissionId: string) => ['admission-preauth-status', admissionId] as const;

export function useAdmission(admissionId: string) {
  return useQuery({
    queryKey: admissionKey(admissionId),
    queryFn: () => get<Admission>(`/ipd/admissions/${admissionId}`),
    enabled: Boolean(admissionId),
  });
}

export function useCareTeam(admissionId: string, enabled = true) {
  return useQuery({
    queryKey: careTeamKey(admissionId),
    queryFn: () => get<AdmissionCareTeamDto>(`/ipd/admissions/${admissionId}/care-team`),
    enabled: Boolean(admissionId) && enabled,
    staleTime: 60_000,
  });
}

export function useCareTeamCandidates(branchId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['care-team-candidates', branchId ?? 'working'],
    queryFn: () => get<CareTeamCandidateDto[]>(`/ipd/care-team/candidates${qs({ branchId })}`),
    enabled,
    staleTime: 300_000,
  });
}

export function usePreAuthStatus(admissionId: string, enabled: boolean) {
  return useQuery({
    queryKey: preAuthKey(admissionId),
    queryFn: () => get<AdmissionPreAuthStatusDto>(`/insurance/admissions/${admissionId}/preauth-status`),
    enabled: Boolean(admissionId) && enabled,
    staleTime: 60_000,
  });
}

export function useAdmissionLive(admissionId: string): void {
  useRealtimeInvalidate(admissionId ? [`admission:${admissionId}`] : [], ['care-team.changed'], [admissionKey(admissionId), careTeamKey(admissionId)]);
}
